import { Controller } from "@hotwired/stimulus";
import { playerFor, seekPlayer, parseTime, timeFromHash } from "modules/player_seek";

// Time links for the Bitmovin players on a page (Rob, 2026-09-30 and
// 2026-10-09). Mounted once on every presentation by presentations/_show, so
// a content block needs no Stimulus markup: any link to a time on this page,
// `<a href="#t=03:12">`, seeks the video there, plays it and scrolls it into
// view instead of jumping to the anchor. Arriving with `#t=…` in the address
// seeks there too, paused (browsers refuse to autoplay anyway). The seek is
// modules/player_seek.js, the same call presenter mode makes.
//
//   <ul class="fv-chapters">
//     <li><a href="#t=00:00">00:00 Introductie</a></li>
//     <li><a href="#t=1:02:03">1:02:03 Slot</a></li>
//   </ul>
//
// Which player: a link seeks the nearest video above it on the page (the
// usual chapter list under its video), or the first one when there is none
// above. The address seeks the first video in this presentation.
// `data-video-chapters-player-value="<player element id>"` on the mount, or
// `data-player="<id>"` on one link, overrides both. Times read as `h:mm:ss`,
// `mm:ss` or plain seconds; `data-time` on a link overrides its hash.
//
// The 2026-09-30 markup pasted into raw HTML widgets
// (`data-controller="video-chapters"`, `data-action="video-chapters#seek"`)
// keeps working: a nested instance only answers its own actions.
export default class extends Controller {
  static values = { player: String };

  connect() {
    this.nested = Boolean(this.element.parentElement?.closest('[data-controller~="video-chapters"]'));
    if (this.nested) return;

    this.onClick = (event) => this.delegatedSeek(event);
    this.onHashChange = () => this.jumpToHash();
    this.onPlayerReady = (event) => this.seekPendingHash(event.detail?.player);

    this.element.addEventListener("click", this.onClick);
    window.addEventListener("hashchange", this.onHashChange);
    window.addEventListener("mama:player-ready", this.onPlayerReady);
    this.jumpToHash();
  }

  disconnect() {
    if (this.nested) return;

    this.element.removeEventListener("click", this.onClick);
    window.removeEventListener("hashchange", this.onHashChange);
    window.removeEventListener("mama:player-ready", this.onPlayerReady);
  }

  // data-action="video-chapters#seek", the 2026-09-30 markup.
  seek(event) {
    this.seekFromLink(event, event.currentTarget);
  }

  delegatedSeek(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest?.("a[href]");
    if (!link || !this.element.contains(link) || !this.onThisPage(link)) return;

    this.seekFromLink(event, link);
  }

  seekFromLink(event, link) {
    const seconds = link.dataset.time ? parseTime(link.dataset.time) : timeFromHash(link.hash);
    if (seconds === null) return;

    const element = this.playerElementForLink(link);
    if (!element) return;

    event.preventDefault();
    if (link.hash) history.replaceState(null, "", link.hash);
    this.jump(element, seconds, true);
  }

  jumpToHash() {
    const seconds = timeFromHash(location.hash);
    if (seconds === null) return;

    const element = this.playerElementForAddress();
    if (!element) return;

    if (playerFor(element)) {
      this.pendingHash = null;
      this.jump(element, seconds, false);
    } else {
      // The player script has not run yet; seekPendingHash takes it from its Ready.
      this.pendingHash = { elementId: element.id, seconds };
    }
  }

  seekPendingHash(player) {
    if (!this.pendingHash || !player) return;

    const element = document.getElementById(this.pendingHash.elementId);
    if (playerFor(element) !== player) return;

    const { seconds } = this.pendingHash;
    this.pendingHash = null;
    this.jump(element, seconds, false);
  }

  jump(element, seconds, play) {
    const player = playerFor(element);
    if (!player) return;

    // Seek first: it swaps a poster for the player, which changes the height to scroll by.
    seekPlayer(player, seconds, { play });
    this.scrollTo(element);
  }

  onThisPage(link) {
    return link.origin === location.origin && link.pathname === location.pathname;
  }

  playerElementForLink(link) {
    const chosen = link.dataset.player || this.playerValue;
    if (chosen) return document.getElementById(chosen);

    const all = Array.from(document.querySelectorAll(".video-player"));
    const above = all.filter(
      (element) => element.compareDocumentPosition(link) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    return above[above.length - 1] || all[0] || null;
  }

  playerElementForAddress() {
    if (this.playerValue) return document.getElementById(this.playerValue);
    return this.element.querySelector(".video-player");
  }

  // Same offset as the presenter's scroll_to: the fixed navbar, when there is one.
  scrollTo(element) {
    const target = element.closest(".video-player-wrapper") || element;
    const navbar = document.querySelector("nav.navbar");
    const headerOffset = navbar ? navbar.getBoundingClientRect().height : 0;
    const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top, behavior: "smooth" });
  }
}
